import React, { useState } from "react";
import axios from "axios";
import config from "../../config";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import {
  FaLocationArrow,
  FaRegClock,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import "./CareersPage.css";

const jobs = {
  sales: [
    {
      title: "Key Account Sales Representative",
      location: "Los Angeles, CA",
      roleOverview:
        "As a Key Account Sales Representative, you’ll play a crucial role in growing our company. Your focus will be on onboarding restaurant kitchens to the marketplace and connecting food entrepreneurs to rent these spaces.",
      responsibilities: [
        "Identify and onboard restaurant kitchens to list on the platform.",
        "Build relationships with kitchen owners and food entrepreneurs to create a network of clients.",
        "Manage and promote the kitchens you onboard, ensuring they attract renters.",
        "Act as a representative of AirKitchenz AI, educating clients on the benefits of joining the platform.",
        "Develop strategies to connect food entrepreneurs with kitchens and secure rentals.",
        "Meet and exceed monthly onboarding targets to unlock additional bonuses.",
      ],
      whoWeAreLookingFor:
        "We’re not looking for extensive sales experience or flashy resumes. We want people who know the food and beverage industry and have the passion to make a difference. Whether you're a server, bartender, restaurant manager or front-of-house worker eager to transition into the tech sector, a sales professional in wine, liquor, or food service with a book of clients and direct access to restaurants, or a tech or other sales professional interested in breaking into an innovative new market, if you have knowledge of the industry and the drive to succeed, this is the perfect opportunity to make a mark.",
      whyJoinUs:
        "AirKitchenz AI is the first of its kind—an untapped market with no competition and endless possibilities. If you’re ready to bring this concept to life and help restaurants thrive, you could be part of something huge.",
      locationDetails:
        "This position is hybrid—mostly remote—with in-person meetings or visits. Candidates must be familiar with the LA and Orange County areas as we launch in these regions.",
      compensation: [
        "Flat fee of $50 for every restaurant kitchen you successfully onboard to the platform.",
        "Earn 10% commission on rentals for the first six months for the kitchens you onboard.",
        "Incentive Bonuses: Onboard more than 50 kitchens per month to receive a 10% bonus on your earnings for that month.",
        "Commission increases to 12% for three months on rentals for exceeding monthly onboarding targets.",
        "Monthly Car Allowance provided to support travel and client visits.",
      ],
      additionalDetails:
        "This is a commission-based, 1099 contractor position, offering flexibility and the ability to control your earnings based on performance.",
      applyLink:
        "https://www.linkedin.com/jobs/view/4146237938",
    },
  ],
  engineering: [
    {
    title: "Software Engineering Intern (React Native)  ",
    location: "Los Angeles, CA",
    roleOverview:
      "At AirKitchenz, we’re revolutionizing the food industry by connecting restaurants with food entrepreneurs through our tech-driven marketplace. We’re looking for a Software Engineering Intern to help us build and enhance our React Native mobile app for iOS and Android.",
    responsibilities: [
      "Assist in developing and maintaining our React Native mobile app.",
      "Build new features and optimize app performance for a smooth user experience.",
      "Work with RESTful APIs and integrate backend services.",
      "Collaborate with our engineering team in code reviews, brainstorming sessions, and agile development.",
      "Contribute to UI/UX enhancements for a user-friendly experience.",
      "Stay updated on the latest React Native trends and best practices.",
    ],
    whoWeAreLookingFor: 
      "We are looking for candidates who are currently pursuing or have recently completed a degree in Computer Science, Software Engineering, or a related field. Ideal candidates should have experience with React Native through personal projects, coursework, or prior internships, along with familiarity with AWS, JavaScript, TypeScript, and React as a plus. A strong understanding of mobile UI design principles, a passion for startups, problem-solving, and building innovative products is essential. Additionally, we seek individuals who are self-motivated and capable of working independently in a fast-paced startup",
    whyJoinUs:
      "AirKitchenz AI is the first of its kind—an untapped market with no competition and endless possibilities. If you’re ready to bring this concept to life and help restaurants thrive, you could be part of something huge.",
    locationDetails:
      "This position is hybrid—mostly remote—with in-person meetings or visits. Candidates must be familiar with the LA and Orange County areas as we launch in these regions.",
    compensation: [
      "We’re looking for enthusiastic individuals eager to gain hands-on experience. Please note this is an unpaid internship.",
    ],
    additionalDetails:
      " This is a unique opportunity to gain hands-on experience and work on an impactful product alongside a passionate team. If you're excited about food tech, startups, and building products from the ground up, we'd love to hear from you! ",
    applyLink:
      "https://www.linkedin.com/jobs/view/4170316182",
  },
],
  marketing: [],
};

const ApplicationForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    currentPosition: "",
    message: "",
    resume: null,
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle file input changes
  const handleFileChange = (e) => {
    setFormData({ ...formData, resume: e.target.files[0] });
  };

  // Handle form submission – similar to ContactUs.js
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      currentPosition: formData.currentPosition,
      message: formData.message,
      resume: formData.resume ? formData.resume.name : "",
    };

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/api/job-application`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      setSuccessMessage(response.data.message);
      setFormData({
        name: "",
        email: "",
        phone: "",
        currentPosition: "",
        message: "",
        resume: null,
      });
    } catch (error) {
      console.error("Error submitting application:", error);
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to submit application. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="application-form" onSubmit={handleSubmit}>
      <h2>Send Your Application</h2>
      <input
        type="text"
        name="name"
        placeholder="Your Name"
        required
        value={formData.name}
        onChange={handleChange}
      />
      <input
        type="email"
        name="email"
        placeholder="Your Email"
        required
        value={formData.email}
        onChange={handleChange}
      />
      <input
        type="tel"
        name="phone"
        placeholder="Phone (optional)"
        value={formData.phone}
        onChange={handleChange}
      />
      <input
        type="text"
        name="currentPosition"
        placeholder="Current Position"
        required
        value={formData.currentPosition}
        onChange={handleChange}
      />
      <textarea
        name="message"
        placeholder="Why are you a great fit?"
        required
        value={formData.message}
        onChange={handleChange}
      ></textarea>
      <label htmlFor="resume" className="file-label">
        Upload Resume/CV
      </label>
      <input
        type="file"
        name="resume"
        id="resume"
        onChange={handleFileChange}
        required
      />
      <button type="submit">Submit Application</button>
    </form>
  );
};

const CareersPage = () => {
  const [category, setCategory] = useState("sales");
  const [activeJobIndex, setActiveJobIndex] = useState(null);

  const toggleJobDescription = (index) => {
    setActiveJobIndex(activeJobIndex === index ? null : index);
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    setActiveJobIndex(null);
  };

  return (
    <div className="career-page">
      <Navbar />

      {/* Hero Section */}
      <section className="career-hero-section">
        <div className="career-hero-overlay">
          <h1>Join our team and be a part of a success story.</h1>
          <p>Build the future with us.</p>
          <button
            onClick={() => window.scrollTo({ top: 500, behavior: "smooth" })}
          >
            View Open Positions
          </button>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="categories-wrapper">
          <div className="categories-tabs">
            {["sales", "engineering", "marketing"].map((cat, idx) => (
              <button
                key={idx}
                className={category === cat ? "active" : ""}
                onClick={() => handleCategoryChange(cat)}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
          <div className="categories-dropdown">
            <select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="sales">Sales</option>
              <option value="engineering">Engineering</option>
              <option value="marketing">Marketing</option>
            </select>
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="jobs-section">
        {jobs[category] && jobs[category].length > 0 ? (
          <div className="job-grid">
            {jobs[category].map((job, index) => (
              <div className="job-card" key={index}>
                <div
                  className="job-card-header"
                  onClick={() => toggleJobDescription(index)}
                >
                  <h2 className="job-title">{job.title}</h2>
                  <div className="toggle-icon">
                    {activeJobIndex === index ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </div>
                </div>
                <div className="job-meta">
                  <span>
                    <FaLocationArrow /> {job.location}
                  </span>
                  <span>
                    <FaRegClock /> Full-time
                  </span>
                </div>
                {activeJobIndex === index && (
                  <div className="job-details-dropdown">
                    <section className="job-detail-section">
                      <h3>Role Overview</h3>
                      <p>{job.roleOverview}</p>
                    </section>
                    <section className="job-detail-section">
                      <h3>Key Responsibilities:</h3>
                      <ul>
                        {job.responsibilities.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </section>
                    <section className="job-detail-section">
                      <h3>Who We’re Looking For:</h3>
                      <p>{job.whoWeAreLookingFor}</p>
                    </section>
                    <section className="job-detail-section">
                      <h3>Why join us?</h3>
                      <p>{job.whyJoinUs}</p>
                    </section>
                    <section className="job-detail-section">
                      <h3>Compensation:</h3>
                      <p>{job.compensation}</p>
                    </section>
                    <section className="job-detail-section">
                      <h3>Additional Details:</h3>
                      <p>{job.additionalDetails}</p>
                    </section>
                  </div>
                )}
                <a
                  href={
                    job.applyLink ||
                    `https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(
                      job.title
                    )}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="apply-link"
                >
                  Apply on LinkedIn
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-jobs">
            <p>
              There are no open positions in {category} at the moment. We’d
              still love to hear from you.
            </p>
            <ApplicationForm />
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
